import { defineStore } from "pinia";
import {
  type ImportsResponse,
  RequestsImportApi,
  type UploadRequestsCSVRequest,
  RequestsApi,
  AIAgentsApi,
  MessagesApi,
  type RequestLinkableBookingItem,
  type UpdateRequestDto,
} from "@simbelapp/order-sdk";
import { ConfirmPriorityDtoPriorityEnum } from "@simbelapp/order-sdk";
import type { IGetSelectedRequests, IRequestStore } from "~/utils/interfaces/requests-interfaces";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { useEmployeeTrainingsStore } from "~/store/employee-trainings/employee-trainings.store";
import { RequestStatus } from "~/utils/enums/request.enums";
import { usePagination } from "~/composables/paginations/usePagination";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { PaginationKeysEnum } from "~/utils/enums/pagination-keys.enums";
import { AIAgentRolesEnum } from "~/utils/enums/ai-agent.enums";
import { useFilters } from "~/composables/filters/useFilters";

export const useRequestsStore = defineStore("requestsStore", {
  state: (): IRequestStore => {
    return {
      requests: null,
      requestDetails: null,
      collaborators: [],
      messages: [],
      showRefuseModal: false,
      showCancelModal: false,
      showRemoveModal: false,
      showPauseModal: false,
      showEditCostModal: false,
      showCsvImportModal: false,
      showDuplicatedModal: false,
      showAIAgentModal: false,
      AIAgentContextId: null,
      AIAgentMessages: [],
      AIAgentUserQuery: null,
      selectedRequestsList: [],
      excludedRequestsList: [],
      duplicatedRequests: [],
      bookingRecommendation: null,
      showBookingRecommendationsModal: false,
      bookingRecommendations: [],
      requestsToProcess: [],
    };
  },
  getters: {},
  actions: {
    /**
     * Fetches all requests with pagination and filters
     * @returns {Promise<void>}
     */
    async fetchRequests(): Promise<void> {
      const pagination = usePagination(PaginationKeysEnum.REQUESTS_PAGINATION);
      const filters = useFilters();

      const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);
      const paginateQuery = new URLSearchParams(paginateOptions);
      const queryString = Object.fromEntries(paginateQuery.entries());
      const filtersQuery = filters.getFiltersForSDK();

      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, RequestsApi);
        const response = await apiInstance.findAllCompaniesRequests({
          ...queryString,
          ...filtersQuery,
          sortingColumn: paginateOptions.sortingColumn,
        });

        if (response) {
          this.requests = response;
        }
      } catch {
        feedback.error(`erreur interne`, "small");
      }
    },

    /**
     * Exports the current (filtered) requests list as CSV
     * @returns {Promise<string | null>} Raw CSV content, or null on failure
     */
    async exportRequestsCsv(): Promise<string | null> {
      const pagination = usePagination(PaginationKeysEnum.REQUESTS_PAGINATION);
      const filters = useFilters();
      const feedback = useFeedback();

      const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);
      delete paginateOptions.limit;
      delete paginateOptions.offset;
      const queryString = Object.fromEntries(new URLSearchParams(paginateOptions).entries());
      const filtersQuery = filters.getFiltersForSDK();

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, RequestsApi);
        return await apiInstance.exportRequestsCSV({
          ...queryString,
          ...filtersQuery,
          sortingColumn: paginateOptions.sortingColumn,
          disableLimit: "t",
        });
      } catch {
        feedback.error("Une erreur est survenue", "small");
        return null;
      }
    },

    /**
     * Exports the current (filtered) requests list as XLSX
     * @returns {Promise<Blob | null>} XLSX file blob, or null on failure
     */
    async exportRequestsXlsx(): Promise<Blob | null> {
      const pagination = usePagination(PaginationKeysEnum.REQUESTS_PAGINATION);
      const filters = useFilters();
      const feedback = useFeedback();

      const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);
      delete paginateOptions.limit;
      delete paginateOptions.offset;
      const queryString = Object.fromEntries(new URLSearchParams(paginateOptions).entries());
      const filtersQuery = filters.getFiltersForSDK();

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, RequestsApi);
        return await apiInstance.exportRequestsXLSX({
          ...queryString,
          ...filtersQuery,
          sortingColumn: paginateOptions.sortingColumn,
          disableLimit: "t",
        });
      } catch {
        feedback.error("Une erreur est survenue", "small");
        return null;
      }
    },

    /**
     * Handles AI agent history interaction for user queries
     * Adds user query to messages and retrieves agent response
     * @returns {Promise<void>}
     */
    async historyAIAgent(): Promise<void> {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AIAgentsApi);
      try {
        if (this.requestDetails?.userId && this.AIAgentUserQuery) {
          this.AIAgentMessages.push({
            message: this.AIAgentUserQuery,
            role: AIAgentRolesEnum.USER,
          });

          const result = await apiInstance.userHistoryAgent({
            aIAgentHistoryDTO: {
              userId: this.requestDetails.userId,
              userQuery: this.AIAgentUserQuery,
              requestId: this.requestDetails.requestId,
              ...(this.AIAgentContextId && { contextId: this.AIAgentContextId }),
            },
          });

          this.AIAgentUserQuery = null;

          if (result?.agentResponse && result?.contextId) {
            this.AIAgentContextId = result.contextId;
            this.AIAgentMessages.push({
              message: result.agentResponse ?? "",
              role: AIAgentRolesEnum.SYSTEM,
            });
          }
        }
      } catch (error) {
        console.log(error);
      }
    },

    /**
     * Validates if a request can be processed using AI agent
     * Checks request validity and returns agent response
     * @returns {Promise<void>}
     */
    async validateIfRequestCanBeProcessed(): Promise<void> {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AIAgentsApi);
      try {
        if (this.requestDetails?.userId && this.AIAgentUserQuery) {
          this.AIAgentMessages.push({
            message: this.AIAgentUserQuery,
            role: AIAgentRolesEnum.USER,
          });

          const result = await apiInstance.requestValidatorAgent({
            aIAgentRequestValidatorDTO: {
              userId: this.requestDetails.userId,
              requestId: this.requestDetails.requestId,
              ...(this.AIAgentContextId && { contextId: this.AIAgentContextId }),
            },
          });

          this.AIAgentUserQuery = null;

          if (result?.agentResponse && result?.contextId) {
            this.AIAgentContextId = result.contextId;
            this.AIAgentMessages.push({
              message: result.agentResponse ?? "",
              role: AIAgentRolesEnum.SYSTEM,
            });
          }
        }
      } catch (error) {
        console.log(error);
      }
    },

    /**
     * Fetches detailed information for a specific request
     * @param {string} requestId - The ID of the request to fetch
     * @returns {Promise<void>}
     */
    async fetchRequestDetails(requestId: string): Promise<void> {
      const feedback = useFeedback();
      try {
        this.requestDetails = null;
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, RequestsApi);
        const data = await apiInstance.getRequestDetails({
          requestId,
        });
        this.requestDetails = data;

        if (this.requestDetails.notifications) {
          this.requestDetails.notifications = {
            ...this.requestDetails.notifications,
            refusedEmployeeRequest: false,
            refusedManagerRequest: false,
          };
        }
      } catch (error) {
        // `/requests/{id}` is the only source for `requestDetails`, and the whole detail page body is
        // gated on it (`v-if="requestDetails"`). Swallowing the failure silently left the user on a
        // blank page with just the header — indefinitely, with nothing to act on — whenever the call
        // 404'd (request deleted meanwhile) or 5xx'd. Surface it like every other action here does.
        feedback.error("Une erreur est survenue", "small");
        console.log(error);
      }
    },

    /**
     * Fetches, among the given requests, those an existing booking already answers
     * @param {string[]} requestIds - The IDs of the requests
     * @returns {Promise<RequestLinkableBookingItem[]>} One recommendation per request that has one
     */
    async fetchBookingRecommendations(requestIds: string[]): Promise<RequestLinkableBookingItem[]> {
      if (!requestIds.length) return [];

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, RequestsApi);
        return await apiInstance.getRequestsLinkableBookings({
          getRequestsLinkableBookingsDto: { requestIds },
        });
      } catch (error) {
        // Optional: without recommendations a request is processed as before, so a failure must not toast.
        console.log(error);
        return [];
      }
    },

    /**
     * Validates the open request and makes an existing booking of its attendee its own
     * @param {string} bookingId - The ID of the booking to link the request to
     * @returns {Promise<boolean>} whether the link went through
     */
    async validateAndLinkBooking(bookingId: string): Promise<boolean> {
      const feedback = useFeedback();
      const requestId = this.requestDetails?.requestId;
      if (!requestId) return false;

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, RequestsApi);
        await apiInstance.linkRequestToBooking({
          requestId,
          linkRequestToBookingDto: { bookingId },
        });

        this.bookingRecommendation = null;
        await this.fetchRequestDetails(requestId);
        feedback.success("La demande a bien été validée et rattachée à l'inscription existante.", "small");
        return true;
      } catch {
        feedback.error("Une erreur est survenue", "small");
        return false;
      }
    },

    /**
     * Fetches all messages associated with a request
     * @param {string} requestId - The ID of the request
     * @returns {Promise<void>}
     */
    async fetchMessages(requestId: string): Promise<void> {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, MessagesApi);
      try {
        const data = await apiInstance.getMessages({
          itemId: requestId,
          itemType: "request",
        });
        if (data) {
          this.messages = data;
        }
      } catch (error) {
        console.log(error);
      }
    },

    /**
     * Gets selected and excluded request users
     * @returns {Promise<IGetSelectedRequests>} Selected requests data
     */
    async getSelectedUsers(): Promise<IGetSelectedRequests> {
      const pagination = usePagination(PaginationKeysEnum.REQUESTS_PAGINATION);
      const filters = useFilters();
      const feedback = useFeedback();

      const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);
      delete paginateOptions.limit;
      delete paginateOptions.offset;
      const queryString = Object.fromEntries(new URLSearchParams(paginateOptions).entries());
      const filtersQuery = filters.getFiltersForSDK();

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, RequestsApi);
        const data = await apiInstance.getSelectedUserRequests({
          getSelectedUserRequestsDTO: {
            includedRequestsIds: this.selectedRequestsList?.map((r) => r.requestId) ?? [],
            excludedRequestsIds: this.excludedRequestsList?.map((r) => r.requestId) ?? [],
          },
          ...queryString,
          ...filtersQuery,
          sortingColumn: paginateOptions.sortingColumn,
        });

        return {
          selectedRequests: data.selectedRequests ?? [],
          requestsOfUsersWithMultipleRequests: data.requestsOfUsersWithMultipleRequests ?? [],
        };
      } catch {
        feedback.error("Une erreur est survenue", "small");
        return { selectedRequests: [], requestsOfUsersWithMultipleRequests: [] };
      }
    },

    /**
     * Adds manager input (priority and comment) to a request
     * @param {string} requestId - The ID of the request
     * @param {object} input - Manager input data containing priority and managerInput
     * @returns {Promise<any>}
     */
    async addManagerInput(requestId: string, input: { priority: string; managerInput: string }): Promise<any> {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, RequestsApi);
        const data = await apiInstance.addManagerInput({
          requestId,
          addManagerInputDto: {
            priority: input.priority,
            managerInput: input.managerInput,
          },
        });
        await this.fetchRequestDetails(requestId);
        if (this.requestDetails) {
          this.requestDetails.status = data.newState;
          feedback.success("Vos modifications ont bien été sauvegardées", "small");
        }
      } catch (error) {
        feedback.error("Une erreur est survenue", "small");
        console.log(error);
      }
    },

    async confirmPriority(priority: ConfirmPriorityDtoPriorityEnum): Promise<any> {
      const feedback = useFeedback();
      if (!this.requestDetails?.requestId) return;
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, RequestsApi);
        await apiInstance.confirmRequestPriority({
          requestId: this.requestDetails?.requestId,
          confirmPriorityDto: {
            priority,
          },
        });
        await this.fetchRequestDetails(this.requestDetails?.requestId);
        feedback.success("Vos modifications ont bien été sauvegardées", "small");
      } catch (error) {
        feedback.error(`Une erreur est survenue`, "small");
        console.log(error);
      }
    },

    /**
     * Refuses a request with a comment and notification flags
     * @param {string} comment - External HR comment for refusal
     * @param {boolean} refusedEmployeeRequest - Whether to notify the employee
     * @param {boolean} refusedManagerRequest - Whether to notify the manager
     * @returns {Promise<any>}
     */
    async refuseRequest(
      comment: string,
      refusedEmployeeRequest: boolean,
      refusedManagerRequest: boolean,
    ): Promise<any> {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, RequestsApi);
      try {
        if (this.requestDetails?.requestId && this.requestDetails?.notProcessed) {
          await apiInstance.refuseRequests({
            refuseRequestsDto: {
              requestIds: [this.requestDetails.requestId],
              externalHrComment: comment,
              refusedEmployeeRequest: refusedEmployeeRequest,
              refusedManagerRequest: refusedManagerRequest,
            },
          });
          this.fetchRequestDetails(this.requestDetails.requestId);
          feedback.success("Vos modifications ont bien été sauvegardées", "small");
        }
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },

    /**
     * Batch updates the status of multiple requests
     * @param {string[]} requestIds - Array of request IDs to update
     * @param {string} comment - External HR comment
     * @param {RequestStatus} status - New status to apply
     * @param {boolean} [refusedEmployeeRequest] - Optional flag to notify employee
     * @param {boolean} [refusedManagerRequest] - Optional flag to notify manager
     * @returns {Promise<void>}
     */
    async batchPatchRequestsStatus(
      requestIds: string[],
      comment: string,
      status: RequestStatus,
      refusedEmployeeRequest?: boolean,
      refusedManagerRequest?: boolean,
    ): Promise<void> {
      if (requestIds?.length) {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, RequestsApi);
        try {
          switch (status) {
            case RequestStatus.REQUEST_CANCELED: {
              await apiInstance.cancelRequest({
                patchRequestsDto: {
                  requestIds,
                  externalHrComment: comment,
                },
              });
              break;
            }
            case RequestStatus.REQUEST_REFUSED: {
              if (refusedEmployeeRequest !== undefined && refusedManagerRequest !== undefined) {
                await apiInstance.refuseRequests({
                  refuseRequestsDto: {
                    requestIds,
                    externalHrComment: comment,
                    refusedEmployeeRequest,
                    refusedManagerRequest,
                  },
                });
              }
              break;
            }
            default:
              break;
          }
          const paginate = usePagination(PaginationKeysEnum.REQUESTS_PAGINATION);
          await paginate.fetchData();
        } catch (error) {
          console.log(error);
        }
      }
    },

    /**
     * Cancels the current request in requestDetails
     * @returns {Promise<any>}
     */
    async cancelRequest(): Promise<any> {
      if (this.requestDetails?.requestId && this.requestDetails?.notProcessed) {
        const feedback = useFeedback();
        const requestId = this.requestDetails.requestId;
        try {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, RequestsApi);
          const data = await apiInstance.cancelRequest({
            patchRequestsDto: {
              requestIds: [requestId],
            },
          });
          if (data) {
            if (this.requestDetails?.requestId === requestId) {
              this.requestDetails.status = data.newState;
              this.requestDetails.notProcessed = false;
            }
            feedback.success("Vos modifications ont bien été sauvegardées", "small");
          }
        } catch (error) {
          feedback.error("Une erreur est survenue", "small");
          console.log(error);
        }
      }
    },

    /**
     * Permanently deletes multiple requests
     * @param {string[]} requestIds - Array of request IDs to delete
     * @returns {Promise<void>}
     */
    async batchRemoveRequest(requestIds: string[]): Promise<void> {
      if (requestIds?.length) {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, RequestsApi);
        try {
          const feedback = useFeedback();
          const data = await apiInstance.permanentDeleteRequests({
            permanentDeleteRequestsRequest: {
              requestIds,
            },
          });
          if (data) {
            feedback.success("Vos modifications ont bien été sauvegardées", "small");
          }
          const paginate = usePagination(PaginationKeysEnum.REQUESTS_PAGINATION);

          await paginate.fetchData();
        } catch (error) {
          console.log(error);
        }
      }
    },

    /**
     * Batch updates the cost of multiple requests
     * @param {string[]} requestIds - Array of request IDs to update
     * @param {number} priceExclTax - Cost excl. tax to apply to every selected request
     * @returns {Promise<boolean>} Whether the update succeeded, so the caller can keep the modal open on failure
     */
    async updateRequestsCost(requestIds: string[], priceExclTax: number): Promise<boolean> {
      if (!requestIds?.length) return false;

      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, RequestsApi);
        await apiInstance.updateRequestsCost({
          updateRequestsCostDto: {
            requestIds,
            priceExclTax,
          },
        });

        const paginate = usePagination(PaginationKeysEnum.REQUESTS_PAGINATION);
        await paginate.fetchData();

        feedback.success("Vos modifications ont bien été sauvegardées", "small");
        return true;
      } catch (error) {
        feedback.error("Une erreur est survenue", "small");
        console.log(error);
        return false;
      }
    },

    /**
     * Cancels a request initiated by an employee
     * @returns {Promise<any>}
     */
    async cancelEmployeeRequest(): Promise<any> {
      if (this.requestDetails?.requestId && this.requestDetails?.notProcessed) {
        const feedback = useFeedback();
        try {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, RequestsApi);
          const data = await apiInstance.employeeCancelRequest({
            requestId: this.requestDetails.requestId,
          });
          if (data) {
            this.requestDetails.status = data.newState;
            this.requestDetails.notProcessed = false;
            feedback.success("Votre demande a été annulée", "small");
          }
        } catch (error) {
          console.log(error);
        }
      }
    },

    /**
     * Updates a request with new data
     * @param {UpdateEmployeeRequestRequest} - Payload containing update data
     * @returns {Promise<any>}
     */
    async updateRequest(updateRequestDto: UpdateRequestDto): Promise<any> {
      const feedback = useFeedback();

      try {
        if (this.requestDetails?.requestId) {
          const employeeTrainingsStore = useEmployeeTrainingsStore();

          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, RequestsApi);
          await apiInstance.updateEmployeeRequest({
            requestId: this.requestDetails?.requestId,
            updateRequestDto,
          });

          await this.fetchRequestDetails(this.requestDetails?.requestId);
          await employeeTrainingsStore.fetchEmployeeTraining(this.requestDetails?.requestId);
          await employeeTrainingsStore.fetchAllEmployeeTrainings();
          this.requestDetails.notProcessed = false;
          feedback.success("Vos modifications ont bien été sauvegardées", "small");
        } else {
          throw new Error(`La demande n'existe pas`);
        }
      } catch {
        feedback.error("Erreur lors de la mise à jour", "small");
      }
    },

    /**
     * Imports requests from a CSV file
     * @param {UploadRequestsCSVRequest} uploadUsersCSV - CSV file upload request
     * @returns {Promise<ImportsResponse | null>} Import response or null on error
     */
    async importRequests(uploadUsersCSV: UploadRequestsCSVRequest): Promise<ImportsResponse | null> {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, RequestsImportApi);
      const response: ImportsResponse | null = await apiInstance
        .uploadRequestsCSV(uploadUsersCSV)
        .catch(async (error) => {
          const errorResponse = await error.response.json();
          if (errorResponse.message.includes("Parallel import in progress")) {
            feedback.error("Un import est déjà en cours, veuillez attendre avant de relancer", "small");
            return null;
          } else {
            feedback.error("Erreur Interne", "small");
            return null;
          }
        });
      if (response) {
        return response;
      } else {
        return null;
      }
    },

    /**
     * Fetches and adds collaborators for the current request
     * @returns {Promise<void>}
     */
    async getRequestCollaborators(): Promise<void> {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, RequestsApi);
        if (this.requestDetails?.requestId) {
          const data = await apiInstance.addRequestCollaborators({
            requestId: this.requestDetails?.requestId,
          });
          const collaborators = await apiInstance.getRequestCollaborators({
            requestId: this.requestDetails?.requestId,
          });
          if (data) {
            this.collaborators = collaborators;
          }
        }
      } catch (error) {
        console.log(error);
      }
    },

    /**
     * Puts requests on hold/pause
     * @param {string[]} requestIds - Array of request IDs to put on hold
     * @returns {Promise<void>}
     */
    async putRequestOnPause(requestIds: string[]): Promise<void> {
      if (requestIds.length) {
        const feedback = useFeedback();
        try {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, RequestsApi);
          await apiInstance.putRequestsOnHold({
            putOnHoldDto: {
              requestIds,
            },
          });
          feedback.success("Vos modifications ont bien été sauvegardées", "small");
          if (this.requestDetails?.requestId) {
            this.fetchRequestDetails(this.requestDetails.requestId);
          }
        } catch (error) {
          console.log(error);
        }
      }
    },
    async saveInternalComment(hrComment: string): Promise<void> {
      if (this.requestDetails?.requestId && hrComment) {
        const feedback = useFeedback();
        try {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, RequestsApi);
          await apiInstance.addInternalComment({
            requestId: this.requestDetails.requestId,
            addInternalCommentDTO: {
              internalHrComment: hrComment,
            },
          });
          feedback.success("Message interne ajouté", "small");
          this.fetchRequestDetails(this.requestDetails.requestId);
        } catch (error) {
          console.log(error);
          feedback.error(`Une erreur est survenue`, "small");
        }
      }
    },
  },
});
